import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { useAuth, useDialog, useSnack, useUnderMaintenance } from '../state';

import axios, { AxiosError } from 'axios';
import { CheckUnPublishError } from '@/helpers';
import { defaultQueryClientConfig } from '@muvi/react-common';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { setHeaderToken } from '../libs/react-query';
import { storage } from '../libs';
import getConfig from 'next/config';

interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
}

const { publicRuntimeConfig } = getConfig();

export const axiosInstanceRaw = axios.create({
  baseURL: process.env.NEXT_APP_API_URL,
  timeout: 9000,
});

export const QueryClientHandelErrorHook = () => {
  const setUser = useAuth(s => s.setUser, shallow);
  const router = useRouter();
  const setDialog = useDialog(s => s.setDialog, shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const setUnderMaintenanceMode = useUnderMaintenance(
    s => s.setUnderMaintenanceMode,
    shallow
  );

  let isRefreshingToken = false;
  let tokenRefreshPromise: RefreshTokenResponse = null;
  const RefreshTokenUrl = '/auth/auth-refresh';

  const fetchNewTokens = async (): Promise<RefreshTokenResponse> => {
    isRefreshingToken = true;
    return axiosInstanceRaw
      .post(RefreshTokenUrl, null, {
        headers: {
          Authorization: `Bearer ${storage.user.get().refreshToken}`,
          'Accept-Language': storage.user.get().language.toUpperCase(),
        },
      })
      .then(res => {
        isRefreshingToken = false;
        return res.data as RefreshTokenResponse;
      })
      .catch(() => {
        isRefreshingToken = false;
        return null;
      });
  };

  const refreshAuth = async (isMutation: boolean, ErrorUrl: string): Promise<string> => {
    if (
      !isRefreshingToken &&
      !tokenRefreshPromise &&
      !!storage?.user?.get()?.refreshToken
    ) {
      tokenRefreshPromise = await fetchNewTokens();
    }

    let newTokens = null;

    if (!!tokenRefreshPromise?.token) {
      newTokens = Object.assign({}, tokenRefreshPromise);
    }

    if (!newTokens && !isRefreshingToken && !tokenRefreshPromise && !isMutation) {
      router.push('/');
      setDialog(null);
      setUser(null);
      setSnack({
        isOpen: true,
        message: 'global.session_expired',
        type: 'error',
      });
      tokenRefreshPromise = null;
      isRefreshingToken = false;
      return;
    }

    // If it's a query and have this spcific url
    if (ErrorUrl === 'cards/initialize-add-card') {
      location.reload();
    }

    if (!!newTokens) {
      const { token, refreshToken } = newTokens as {
        token: string;
        refreshToken: string;
      };
      setHeaderToken(token);
      setUser(prevState => ({ ...prevState, token, refreshToken }));
      tokenRefreshPromise = null;
      isRefreshingToken = false;
      return token;
    } else {
      // Refresh failed or no tokens returned
      tokenRefreshPromise = null;
      isRefreshingToken = false;
      if (isMutation) {
        setDialog(null);
        setUser(null);
        setSnack({
          isOpen: true,
          message: 'global.session_expired',
          type: 'error',
        });
        // Stay on the current page; do not redirect
        return;
      }
      return Promise.reject();
    }
  };

  const errorHandler = async (
    error: AxiosError<any, any>,
    isMutation: boolean
  ): Promise<any> => {
    if (CheckUnPublishError(error)) {
      router.push('/un-available');
    }

    if (error?.response?.status === 400) {
      if (
        error.response?.data?.code !== 24 &&
        error.response?.data?.code !== 26
      ) {
        // Handle if the case is E-wallet balance isn't enough don't show error message
        setSnack({
          isOpen: true,
          message: error?.response?.data?.message,
          type: 'error',
        });
      }
    }
    if (error?.response?.status === 401) {
      if (
        // check if the case is not the case when email for old user is invalid
        !(
          error?.config?.url === 'auth/send-login-email-verification-code' &&
          error?.response?.data?.code === 1
        )
      ) {
        await refreshAuth(isMutation, error?.config?.url);
      }
      return;
    }
    if (error?.response?.status === 403) {
      // This custom message will appear if the country is black listed
      const customMessage = error?.response?.data?.message || '';
      setSnack({
        isOpen: true,
        message: customMessage || 'global.not_authenticated',
        type: 'error',
      });
    }
    if (error?.response?.status === 503) {
      const underMaintenanceTitle = error?.response?.data?.data?.title;
      const underMaintenanceDescription = error?.response?.data?.data?.description;

      setUnderMaintenanceMode({
        isActive: true,
        title: underMaintenanceTitle ? underMaintenanceTitle : null,
        message: underMaintenanceDescription ? underMaintenanceDescription : null,
      });
    }
    if (error?.response?.status >= 500 && error?.response?.status !== 503) {
      setSnack({
        isOpen: true,
        message: 'global.server_down',
        type: 'error',
      });
    }
  };

  const [queryClient] = useState(
    new QueryClient({
      ...defaultQueryClientConfig,
      defaultOptions: {
        queries: {
          retry: 0,
          refetchOnWindowFocus: false,
        },
        mutations: {
          retry: 0,
        },
      },
      queryCache: new QueryCache({
        onError: error => errorHandler(error as AxiosError<any, any>, false),
      }),
      mutationCache: new MutationCache({
        onError: error => errorHandler(error as AxiosError<any, any>, true),
      }),
    })
  );
  return {
    queryClient,
  };
};
